import { useState } from 'react'
import { ArrowRight, CheckCheck, Lock } from 'lucide-react'
import { getClubAbbr } from '../../clubBanners'
import {
  IGNITE_ROW_STAGGER_MS,
  IGNITE_START_DELAY_MS,
  igniteWaveWouldPlay,
} from './useEntryIgnite'
import type { EntryDockModel } from './entryDockState'
import type { Game } from '../../types'
import './EntryDock.css'

/** How many uncalled fixtures get their own chip before the rest collapse into a `+N`. */
const CHIP_LIMIT = 3

interface EntryDockProps {
  model: EntryDockModel
  /** Which gameweek's wave this panel is waiting on. */
  gameweek: number
  /** The fixture rows the ignite wave covers — the panel's entrance rides the last one. */
  igniteRowCount: number
  /** The Banker fixture, or null when none was set. Formatted here so one rule owns
   *  what a fixture is called on this panel. */
  bankerGame: Game | null
  /** The played chip's name, or null for none. */
  chipLabel: string | null
  /** Open one fixture's sheet on its own — what a chip does. */
  onSelectGame: (gameId: string) => void
  /** Enter the prediction walk at this fixture, so the sheet carries its Next button —
   *  what the primary button does. Finishing a half-called gameweek is then the same
   *  gesture as calling a fresh one from the start CTA. */
  onStartCycleAt: (gameId: string) => void
}

function kickoffLabel(game: Game): string {
  const date = new Date(game.kickoff)
  const weekday = date.toLocaleDateString('en-GB', { weekday: 'short' })
  const time = date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  })
  return `${weekday} ${time}`
}

function matchLabel(game: Game): string {
  return `${getClubAbbr(game.homeTeam)} v ${getClubAbbr(game.awayTeam)}`
}

/** Banker · Chips · Picks, shared by the two finished states. */
function EntryFacts({
  bankerGame,
  chipLabel,
  split,
}: Pick<EntryDockProps, 'bankerGame' | 'chipLabel'> & {
  split: EntryDockModel['split']
}) {
  return (
    <div className="entry-dock__facts">
      <span className="entry-dock__fact">
        <span className="entry-dock__fact-key label-eyebrow">Banker</span>
        <span className="entry-dock__fact-value">
          {bankerGame ? matchLabel(bankerGame) : 'Not set'}
        </span>
      </span>
      <span className="entry-dock__fact-sep" />
      <span className="entry-dock__fact">
        <span className="entry-dock__fact-key label-eyebrow">Chips</span>
        {/* A dash, not "None played": the row is scanned, and three words for an absence
            outweighs the two facts either side of it. */}
        <span className="entry-dock__fact-value">{chipLabel ?? '–'}</span>
      </span>
      <span className="entry-dock__fact-sep" />
      <span className="entry-dock__fact">
        <span className="entry-dock__fact-key label-eyebrow">Picks</span>
        {/* The one read on this screen available nowhere else: did I call four home wins? */}
        <span className="entry-dock__fact-value entry-dock__split">
          <span>
            <b>{split.home}</b>H
          </span>
          <span>
            <b>{split.draw}</b>D
          </span>
          <span>
            <b>{split.away}</b>A
          </span>
        </span>
      </span>
    </div>
  )
}

/**
 * The panel at the foot of the grid: what is left to call, or what the finished entry
 * holds. See `buildEntryDock` for which of the three states applies and when there is none.
 *
 * The picks-in state is where the entry ignite arrives. Its top edge is the ring's own
 * conic gradient unrolled into a line — same four hues, same stop angles — swept in as the wave
 * passes rather than after it lands, which is what the `--entry-dock-delay` below buys.
 */
function EntryDock({
  model,
  gameweek,
  igniteRowCount,
  bankerGame,
  chipLabel,
  onSelectGame,
  onStartCycleAt,
}: EntryDockProps) {
  // Latched at mount, and only for the state the wave actually reaches. Without the
  // latch this reads false again the moment the hook stamps the gameweek as fired, killing
  // the animation halfway; without the wave's own rules it would replay every time the
  // player swiped back to this tab, which is the one thing the re-entry window exists to
  // stop. No wave, no arrival: the panel is simply already there.
  const [arriving] = useState(
    () => model.kind === 'in' && igniteWaveWouldPlay(gameweek)
  )

  if (model.kind === 'togo') {
    const shown = model.remaining.slice(0, CHIP_LIMIT)
    const overflow = model.remaining.length - shown.length
    const next = model.remaining[0]

    return (
      <section className="entry-dock entry-dock--togo">
        <div className="entry-dock__top">
          <p className="entry-dock__eyebrow label-eyebrow">Still to call</p>
          <p className="entry-dock__left label-eyebrow">
            {model.remaining.length} left
          </p>
        </div>
        <div className="entry-dock__chips">
          {shown.map((game) => (
            <button
              key={game.id}
              type="button"
              className="entry-dock__chip"
              onClick={() => onSelectGame(game.id)}
            >
              <span className="entry-dock__chip-match">{matchLabel(game)}</span>
              <span className="entry-dock__chip-when">
                {kickoffLabel(game)}
              </span>
            </button>
          ))}
          {overflow > 0 && (
            <span className="entry-dock__chip entry-dock__chip--more">
              +{overflow}
            </span>
          )}
        </div>
        <button
          type="button"
          className="entry-dock__cta"
          onClick={() => onStartCycleAt(next.id)}
        >
          Predict {matchLabel(next)}
          <ArrowRight aria-hidden="true" />
        </button>
      </section>
    )
  }

  const isLocked = model.kind === 'locked'
  // Catches the last row as the wave passes rather than waiting for it to finish.
  const delayMs =
    IGNITE_START_DELAY_MS +
    Math.max(igniteRowCount - 1, 0) * IGNITE_ROW_STAGGER_MS

  return (
    <section
      className={`entry-dock entry-dock--seal${isLocked ? ' entry-dock--locked' : ''}${
        arriving ? ' entry-dock--arriving' : ''
      }`}
      style={
        arriving
          ? ({ '--entry-dock-delay': `${delayMs}ms` } as React.CSSProperties)
          : undefined
      }
    >
      <span className="entry-dock__edge" aria-hidden="true" />
      <span className="entry-dock__glow" aria-hidden="true" />
      <p className="entry-dock__title">
        {isLocked ? 'LOCKED' : 'ALL PICKS IN'}
        {isLocked ? (
          <Lock aria-hidden="true" />
        ) : (
          <CheckCheck aria-hidden="true" />
        )}
      </p>
      <p className="entry-dock__line">
        {isLocked ? 'Waiting for kickoff.' : 'Editable until deadline.'}
      </p>
      <EntryFacts
        bankerGame={bankerGame}
        chipLabel={chipLabel}
        split={model.split}
      />
    </section>
  )
}

export default EntryDock
